// Upload da FOTO do estudante COM RECORTE (crop) em proporção 3x4.
//
// A foto da carteirinha é impressa num retrato ~3x4; sem recorte, fotos em
// paisagem ou fora de proporção ficam esticadas/estranhas no card. Aqui o aluno
// enquadra a foto num quadro 3x4 antes de salvar — e de quebra a imagem é
// comprimida (reduz KB). Portado do DropFixImage do UEN, mas com o visual do
// portal (sem MUI/fontawesome) e usando a `react-image-crop` que JÁ é dep do UEB.
//
// Props:
//   value     string  — base64 atual (mostra prévia quando existe)
//   onChange  func     — recebe a base64 final (recortada + comprimida)
//   onErro    func     — mensagem de erro (opcional)
//   aspect    number   — proporção do recorte (default 3/4 = foto 3x4)
import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import ReactCrop, { centerCrop, makeAspectCrop } from 'react-image-crop';
import 'react-image-crop/dist/ReactCrop.css';
import { Upload, Crop as CropIcon, X, Check } from 'lucide-react';
import { Field } from './Fields';
import './FotoCropUpload.css';

// Resolução máxima do maior lado do recorte final (foto de carteirinha é pequena;
// acima disso só engorda o base64 sem ganho visível). Qualidade JPEG final.
const MAX_LADO_FINAL = 1000;
const QUALIDADE_FINAL = 0.9;

function centerAspectCrop(mediaWidth, mediaHeight, aspect) {
    return centerCrop(
        makeAspectCrop({ unit: '%', width: 90 }, aspect, mediaWidth, mediaHeight),
        mediaWidth, mediaHeight,
    );
}

// Desenha o recorte num canvas (respeita retina via devicePixelRatio).
function desenharPreview(image, canvas, crop) {
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    const scaleX = image.naturalWidth / image.width;
    const scaleY = image.naturalHeight / image.height;
    const pixelRatio = window.devicePixelRatio || 1;
    canvas.width = Math.floor(crop.width * scaleX * pixelRatio);
    canvas.height = Math.floor(crop.height * scaleY * pixelRatio);
    ctx.scale(pixelRatio, pixelRatio);
    ctx.imageSmoothingQuality = 'high';
    const cropX = crop.x * scaleX;
    const cropY = crop.y * scaleY;
    ctx.save();
    ctx.translate(-cropX, -cropY);
    ctx.drawImage(image, 0, 0, image.naturalWidth, image.naturalHeight, 0, 0, image.naturalWidth, image.naturalHeight);
    ctx.restore();
}

// Comprime a imagem-fonte (redimensiona pra caber em 1920x1080, q0.8).
function comprimirImagem(file) {
    return new Promise((resolve, reject) => {
        if (!file.type.startsWith('image/')) { reject(new Error('O arquivo enviado não é uma imagem.')); return; }
        const reader = new FileReader();
        reader.onload = (event) => {
            const img = new Image();
            img.onload = () => {
                const canvas = document.createElement('canvas');
                const ctx = canvas.getContext('2d');
                const maxW = 1920; const maxH = 1080;
                let { width, height } = img;
                if (width > maxW || height > maxH) {
                    if (width / height > maxW / maxH) { height = Math.round((height * maxW) / width); width = maxW; }
                    else { width = Math.round((width * maxH) / height); height = maxH; }
                }
                canvas.width = width; canvas.height = height;
                // PNG com transparência vira JPEG (sem alfa): sem esse fundo branco,
                // as áreas transparentes saem PRETAS. Preenche antes de desenhar.
                ctx.fillStyle = '#ffffff';
                ctx.fillRect(0, 0, width, height);
                ctx.drawImage(img, 0, 0, width, height);
                resolve(canvas.toDataURL('image/jpeg', 0.8));
            };
            img.onerror = () => reject(new Error('Não foi possível ler a imagem.'));
            img.src = event.target.result;
        };
        reader.onerror = () => reject(new Error('Falha ao carregar o arquivo.'));
        reader.readAsDataURL(file);
    });
}

export default function FotoCropUpload({ value, onChange, onErro, aspect = 3 / 4 }) {
    const aviso = onErro || (() => {});
    const [imgSrc, setImgSrc] = useState('');
    const [crop, setCrop] = useState();
    const [completedCrop, setCompletedCrop] = useState();
    const [open, setOpen] = useState(false);
    const imgRef = useRef(null);
    const previewCanvasRef = useRef(null);

    const onFile = async (e) => {
        const file = e.target.files?.[0];
        e.target.value = ''; // permite reescolher o mesmo arquivo
        if (!file) return;
        if (!file.type.startsWith('image/')) return aviso('Envie uma imagem (JPG ou PNG).');
        if (file.size > 8 * 1024 * 1024) return aviso('A imagem deve ter no máximo 8 MB.');
        try {
            const dataUrl = await comprimirImagem(file);
            setCrop(undefined);
            setCompletedCrop(undefined);
            setImgSrc(dataUrl);
            setOpen(true);
        } catch (err) {
            aviso(err?.message || 'Não foi possível processar a imagem.');
        }
    };

    const onImageLoad = (e) => {
        const { width, height } = e.currentTarget;
        const pct = centerAspectCrop(width, height, aspect);
        setCrop(pct);
        // Já deixa um recorte concluído (em px) pronto — o botão Salvar funciona
        // sem o aluno precisar arrastar nada.
        setCompletedCrop({
            unit: 'px',
            x: (pct.x / 100) * width,
            y: (pct.y / 100) * height,
            width: (pct.width / 100) * width,
            height: (pct.height / 100) * height,
        });
    };

    // Redesenha a prévia quando o recorte muda (debounce leve).
    useEffect(() => {
        const t = setTimeout(() => {
            if (completedCrop?.width && completedCrop?.height && imgRef.current && previewCanvasRef.current) {
                desenharPreview(imgRef.current, previewCanvasRef.current, completedCrop);
            }
        }, 100);
        return () => clearTimeout(t);
    }, [completedCrop]);

    const fechar = () => { setImgSrc(''); setOpen(false); };

    const salvar = () => {
        const origem = previewCanvasRef.current;
        if (!origem) return;
        let w = origem.width; let h = origem.height;
        const maior = Math.max(w, h);
        if (maior > MAX_LADO_FINAL) { const f = MAX_LADO_FINAL / maior; w = Math.round(w * f); h = Math.round(h * f); }
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        canvas.width = w; canvas.height = h;
        ctx.imageSmoothingEnabled = true;
        ctx.imageSmoothingQuality = 'high';
        ctx.fillStyle = 'white'; // evita fundo transparente virar preto no JPEG
        ctx.fillRect(0, 0, w, h);
        ctx.drawImage(origem, 0, 0, w, h);
        canvas.toBlob((blob) => {
            if (!blob) return;
            const reader = new FileReader();
            reader.onload = () => { onChange(reader.result); fechar(); };
            reader.readAsDataURL(blob);
        }, 'image/jpeg', QUALIDADE_FINAL);
    };

    return (
        <Field label="Foto da carteirinha (3x4)">
            <label className="ueb-foto">
                {value
                    ? <img src={value} alt="Prévia da foto" className="ueb-foto-preview" />
                    : <span className="ueb-foto-ph"><Upload size={18} /> Enviar foto e recortar (3x4)</span>}
                <input type="file" accept="image/*" onChange={onFile} hidden />
            </label>
            {value && <span className="ueb-foto-troca"><CropIcon size={13} /> Clique na foto para trocar e recortar de novo</span>}

            {open && createPortal(
                <div className="ueb-crop-overlay ueb-portal" role="dialog" aria-modal="true" aria-label="Ajustar foto">
                    <div className="ueb-crop-modal">
                        <div className="ueb-crop-head">
                            <strong><CropIcon size={16} /> Ajustar foto (3x4)</strong>
                            <button type="button" className="ueb-crop-x" onClick={fechar} aria-label="Fechar"><X size={16} /></button>
                        </div>

                        <div className="ueb-crop-body">
                            <p className="ueb-crop-hint">Arraste e ajuste o quadro. <b>O que estiver dentro</b> vira a sua foto.</p>

                            {!!imgSrc && (
                                <div className="ueb-crop-stage">
                                    <ReactCrop
                                        crop={crop}
                                        onChange={(_, percentCrop) => setCrop(percentCrop)}
                                        onComplete={(c) => setCompletedCrop(c)}
                                        aspect={aspect}
                                        keepSelection
                                    >
                                        <img ref={imgRef} alt="Recorte" src={imgSrc} className="ueb-crop-img" onLoad={onImageLoad} />
                                    </ReactCrop>
                                </div>
                            )}

                            <div className="ueb-crop-prev-row">
                                <div className="ueb-crop-prev-box">
                                    <canvas ref={previewCanvasRef} className="ueb-crop-prev-canvas" />
                                </div>
                                <div className="ueb-crop-prev-txt">
                                    <b>Prévia</b>
                                    <span>É assim que sai na carteirinha.</span>
                                </div>
                            </div>
                        </div>

                        <div className="ueb-crop-foot">
                            <button type="button" className="pbtn pbtn--ghost" onClick={fechar}>Cancelar</button>
                            <button type="button" className="pbtn pbtn--primary" onClick={salvar} disabled={!completedCrop} style={{ flex: 1 }}>
                                <Check size={16} /> Salvar foto
                            </button>
                        </div>
                    </div>
                </div>,
                document.body,
            )}
        </Field>
    );
}
